// src/pages/MyProfile/components/ProfileContent.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";

//import { useTheme } from "@mui/material/styles";
import { ref, onValue, off, update } from "firebase/database";
import { database } from "../../../firebase";

// Import komponen uploader yang baru
import AvatarUploader from "./AvatarUploader";
import AvatarCoverUploader from "./AvatarCoverUploader";

// Helper: ubah onValue menjadi Promise (untuk fetch data dari Firebase)
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

const ProfileContent = ({ user, theme }) => {
    const [dbUserData, setDbUserData] = useState(null);
    const [userIndex, setUserIndex] = useState('N/A');
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // State untuk dialog edit nama dan gender
    const [openEditProfileDialog, setOpenEditProfileDialog] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedGender, setEditedGender] = useState('');
    const [savingProfile, setSavingProfile] = useState(false); // State for saving profile loading

    useEffect(() => {
        if (user) {
            setLoadingUserData(true);
            const userDbRef = ref(database, `db_user/tb_user/${user.uid}`);
            const unsubscribeDb = onValue(userDbRef, async (snapshot) => {
                const userData = snapshot.val();
                if (userData) {
                    setDbUserData(userData);
                    setEditedName(userData.name || user.displayName || (user.email ? user.email.split('@')[0] : ''));
                    setEditedGender(userData.gender || '');

                    const indexRef = ref(database, "db_config/db_wfms/tb_index");
                    const indexSnapshot = await onValuePromise(indexRef);
                    const indexData = indexSnapshot.val();
                    if (indexData) {
                        const foundIndex = Object.values(indexData).find(item => item.user_uid === user.uid);
                        if (foundIndex) {
                            setUserIndex(foundIndex.nomor_index);
                        } else {
                            setUserIndex('N/A');
                        }
                    } else {
                        setUserIndex('N/A');
                    }
                } else {
                    setDbUserData(null);
                    setUserIndex('N/A');
                    setEditedName(user.displayName || (user.email ? user.email.split('@')[0] : ''));
                    setEditedGender('');
                }
                setLoadingUserData(false);
            }, (error) => {
                console.error("Error fetching user data from DB:", error);
                setDbUserData(null);
                setUserIndex('N/A');
                setEditedName(user.displayName || (user.email ? user.email.split('@')[0] : ''));
                setEditedGender('');
                setLoadingUserData(false);
            });

            return () => off(userDbRef, 'value', unsubscribeDb);
        } else {
            setDbUserData(null);
            setUserIndex('N/A');
            setEditedName('');
            setEditedGender('');
            setLoadingUserData(false);
        }
    }, [user]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // --- Handler for Edit Profile Dialog (Name and Gender) ---
    const handleOpenEditProfileDialog = () => {
        setEditedName(dbUserData?.name || user?.displayName || (user?.email ? user.email.split('@')[0] : ''));
        setEditedGender(dbUserData?.gender || '');
        setOpenEditProfileDialog(true);
    };

    const handleCloseEditProfileDialog = () => {
        setOpenEditProfileDialog(false);
        setEditedName(dbUserData?.name || user?.displayName || (user?.email ? user.email.split('@')[0] : '')); // Reset to current data
        setEditedGender(dbUserData?.gender || ''); // Reset to current data
    };

    const handleSaveProfile = async () => {
        if (!user) {
            setSnackbar({ open: true, message: "Tidak ada pengguna yang login.", severity: "error" });
            return;
        }
        if (!editedName.trim()) {
            setSnackbar({ open: true, message: "Nama tidak boleh kosong.", severity: "warning" });
            return;
        }

        setSavingProfile(true);
        try {
            const userRef = ref(database, `db_user/tb_user/${user.uid}`);
            await update(userRef, {
                name: editedName,
                gender: editedGender
            });
            setSnackbar({ open: true, message: "Profil berhasil diperbarui!", severity: "success" });
            handleCloseEditProfileDialog();
        } catch (error) {
            console.error("Error updating profile:", error);
            setSnackbar({ open: true, message: "Gagal memperbarui profil.", severity: "error" });
        } finally {
            setSavingProfile(false);
        }
    };


    if (loadingUserData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    const userName = dbUserData?.name || user?.displayName || (user?.email ? user.email.split('@')[0] : 'Pengguna');
    const userEmail = user?.email || 'Tidak ada email';
    const userAvatarUrl = dbUserData?.avatar || user?.photoURL;
    const userAvatarCoverUrl = dbUserData?.avatar_cover;

    return (
        <Box>
            <Card sx={{ mb: 0 }}>
                {/* Gunakan komponen AvatarCoverUploader */}
                <AvatarCoverUploader
                    user={user}
                    theme={theme}
                    database={database}
                    currentAvatarCoverUrl={userAvatarCoverUrl}
                    setSnackbar={setSnackbar}
                />
                <CardContent sx={{ pt: 0, position: 'relative', '&:last-child': { pb: 0 } }}>
                    {/* Gunakan komponen AvatarUploader */}
                    <AvatarUploader
                        user={user}
                        theme={theme}
                        database={database}
                        currentAvatarUrl={userAvatarUrl}
                        setSnackbar={setSnackbar}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'flex-end' },
                            gap: 5,
                            mt: -10,
                            ml: { xs: 0, sm: '140px' },
                            width: { xs: '100%', sm: 'calc(100% - 140px)' },
                            pb:2
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: { xs: 'center', sm: 'flex-start' },
                                pt: 2,
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0 }}>{userName}</Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 3,
                                    justifyContent: { xs: 'center', sm: 'flex-start' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" fontWeight="medium" color="text.secondary">
                                        {userEmail}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" fontWeight="medium" color="text.secondary">
                                        |
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" fontWeight="medium" color="text.secondary">
                                        {userIndex}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ display: 'flex', gap: 1 }}
                            onClick={handleOpenEditProfileDialog}
                        >
                            <span>Edit</span>
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Dialog untuk mengedit Nama dan Gender */}
            <Dialog open={openEditProfileDialog} onClose={handleCloseEditProfileDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Profil</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nama"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="gender-select-label">Gender</InputLabel>
                        <Select
                            labelId="gender-select-label"
                            id="gender-select"
                            value={editedGender}
                            label="Gender"
                            onChange={(e) => setEditedGender(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            <MenuItem value="Male">Laki-laki</MenuItem>
                            <MenuItem value="Female">Perempuan</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditProfileDialog} disabled={savingProfile}>Batal</Button>
                    <Button onClick={handleSaveProfile} variant="contained" color="primary" disabled={savingProfile}>
                        {savingProfile ? <CircularProgress size={24} color="inherit" /> : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProfileContent;
